import { useEffect, useMemo, useRef, useState } from 'react';
import { useMobileScrollLock, useOutsideClick } from '@/common/hooks';

import {
  getCountries,
  getCountryCallingCode,
} from 'react-phone-number-input/input';
import flags from 'react-phone-number-input/flags';

const regionNames =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const COUNTRIES = getCountries().map((country) => {
  const callingCode = getCountryCallingCode(country);
  const name = regionNames?.of(country) || country;
  return {
    value: `+${callingCode}`,
    label: `+${callingCode} (${country})`,
    name,
    countryCode: country,
    search: `${name} ${country} +${callingCode} ${callingCode}`.toLowerCase(),
  };
});

export function CountrySelect({
  'aria-label': ariaLabel,
  value,
  onChange,
  disabled,
  className,
  buttonClassName = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useMobileScrollLock(isOpen);

  useOutsideClick(containerRef, () => setIsOpen(false), isOpen);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return;
    }
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const filteredCountries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((country) => country.search.includes(q));
  }, [query]);

  const selectedCountry =
    COUNTRIES.find((c) => c.countryCode === value) ||
    COUNTRIES.find((c) => c.countryCode === 'IN');

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-full flex items-center justify-between rounded-input bg-input-background border border-border-default text-text-primary text-body-sm sm:text-body font-sf-pro-display focus:outline-none focus:border-border-active transition-all px-1.5 sm:px-2.5 ${buttonClassName}`}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {flags[selectedCountry?.countryCode] &&
            (() => {
              const Flag = flags[selectedCountry.countryCode];
              return (
                <Flag
                  title={selectedCountry.label}
                  className="w-5 h-3.5 flex-shrink-0 object-cover"
                />
              );
            })()}
          <span className="flex-shrink-0 text-text-primary font-medium">
            {selectedCountry?.value}
          </span>
        </div>
        <svg
          className={`w-3.5 h-3.5 ml-1 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          data-overlay-container="true"
          style={{ overscrollBehavior: 'contain' }}
          className="absolute z-[100] top-[calc(100%+4px)] left-0 min-w-[220px] w-max max-w-[280px] bg-layer-6-elevated border border-border-default rounded-lg shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="p-2 border-b border-border-default shrink-0">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setIsOpen(false);
              }}
              placeholder="Search country or code"
              aria-label="Search country"
              className="w-full h-8 px-2 rounded-md bg-input-background border border-border-default text-body-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-active"
            />
          </div>
          <div
            className="max-h-[220px] overflow-y-auto custom-scrollbar py-1"
            style={{ overscrollBehavior: 'contain' }}
          >
            {filteredCountries.length === 0 ? (
              <p className="px-3 py-2 text-body-sm text-text-muted">
                No countries found
              </p>
            ) : (
              filteredCountries.map((country) => (
                <button
                  key={country.label}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-body-sm font-sf-pro-display transition-colors ${
                    value === country.countryCode
                      ? 'bg-primary/20 text-primary'
                      : 'text-text-primary hover:bg-white/10'
                  }`}
                  onClick={() => {
                    onChange(country.countryCode);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-center gap-2">
                    {flags[country.countryCode] &&
                      (() => {
                        const Flag = flags[country.countryCode];
                        return (
                          <Flag
                            title={country.label}
                            className="w-5 h-4 flex-shrink-0 object-cover"
                          />
                        );
                      })()}
                    <span>{country.label}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
